import { useState, useEffect, useCallback } from 'react'
import {
  fetchCustomReportsCatalog,
  fetchCustomReportById,
  previewCustomReport,
  createCustomReport,
  updateCustomReport,
} from '../../api/customReportsApi'
import { normalizeReportError } from '../../utils/reportErrorUtils'

const BLANK_VISUALIZATION = { type: 'TABLE', xAxis: '', yAxis: '', series: [], title: '' }

export function useCustomReportBuilder({ open, reportId = null, initialPrefill = null, onClose, onSaved }) {
  const [step, setStep] = useState(1)
  const [loadingCatalog, setLoadingCatalog] = useState(true)
  const [catalog, setCatalog] = useState({
    dataSources: [],
    fields: [],
    operators: [],
    visualizations: [],
    calculationTemplates: [],
  })

  // ── Step 1: Setup ──
  const [reportName, setReportName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('General')
  const [companyId, setCompanyId] = useState('')
  const [branchId, setBranchId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [visibility, setVisibility] = useState('PRIVATE')

  // ── Step 2: Data Source & Fields ──
  const [primaryModule, setPrimaryModule] = useState('project')
  const [secondaryDatasets, setSecondaryDatasets] = useState([])
  const [selectedFields, setSelectedFields] = useState([])

  // ── Step 3: Filters ──
  const [filterLogic, setFilterLogic] = useState('AND')
  const [filterConditions, setFilterConditions] = useState([])

  // ── Step 4: Grouping & Sorting ──
  const [grouping, setGrouping] = useState([])
  const [sorting, setSorting] = useState([])

  // ── Step 5: Calculations ──
  const [calculations, setCalculations] = useState([])

  // ── Step 6: Aggregations ──
  const [aggregations, setAggregations] = useState([])

  // ── Step 7: Visualization ──
  const [visualization, setVisualization] = useState(BLANK_VISUALIZATION)

  // ── Step 8: Preview ──
  const [previewData, setPreviewData] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(null)

  // ── Loading an existing report for editing ──
  const [loadingReport, setLoadingReport] = useState(false)
  const [loadReportError, setLoadReportError] = useState(null)

  // ── Saving State ──
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  // Load catalog on mount
  useEffect(() => {
    async function loadCatalog() {
      setLoadingCatalog(true)
      try {
        const cat = await fetchCustomReportsCatalog()
        setCatalog(cat)
      } catch (err) {
        console.error('Failed to load custom reports catalog:', err)
      } finally {
        setLoadingCatalog(false)
      }
    }
    loadCatalog()
  }, [])

  // Reset to a clean slate every time the builder opens, then — if editing —
  // load that report's saved config over it. The modal that hosts this hook
  // stays mounted while closed (it just returns null), so without this reset
  // a second "Create Custom Report" after closing an edit session would
  // otherwise still be carrying the previous report's state around.
  useEffect(() => {
    if (!open) return undefined

    setStep(1)
    setReportName('')
    setDescription('')
    setCategory('General')
    setCompanyId('')
    setBranchId('')
    setDepartmentId('')
    setProjectId('')
    setVisibility('PRIVATE')
    setPrimaryModule('project')
    setSecondaryDatasets([])
    setSelectedFields([])
    setFilterLogic('AND')
    setFilterConditions([])
    setGrouping([])
    setSorting([])
    setCalculations([])
    setAggregations([])
    setVisualization(BLANK_VISUALIZATION)
    setPreviewData(null)
    setPreviewError(null)
    setSaveError(null)
    setLoadReportError(null)

    if (!reportId) return undefined

    let active = true
    async function loadReport() {
      setLoadingReport(true)
      try {
        const report = await fetchCustomReportById(reportId)
        if (!active) return

        setReportName(report.reportName || '')
        setDescription(report.description || '')
        setCategory(report.category || 'General')
        setCompanyId(report.companyId ? String(report.companyId) : '')
        setBranchId(report.branchId ? String(report.branchId) : '')
        setDepartmentId(report.departmentId ? String(report.departmentId) : '')
        setProjectId(report.projectId ? String(report.projectId) : '')
        setVisibility(report.visibility || 'PRIVATE')

        const config = report.config || {}
        setPrimaryModule(config.primaryModule || 'project')
        setSecondaryDatasets(config.secondaryDatasets || [])
        setSelectedFields(config.fields || [])
        setFilterLogic(config.filters?.logic || 'AND')
        setFilterConditions(config.filters?.conditions || [])
        setGrouping(config.grouping || [])
        setSorting(config.sorting || [])
        setCalculations(config.calculations || [])
        setAggregations(config.aggregations || [])
        setVisualization(config.visualization || BLANK_VISUALIZATION)
      } catch (err) {
        if (!active) return
        console.error('Failed to load custom report for editing:', err)
        setLoadReportError(normalizeReportError(err, 'Failed to load this report.'))
      } finally {
        if (active) setLoadingReport(false)
      }
    }
    loadReport()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reportId])

  // Auto-select initial fields when primaryModule changes if no fields chosen or all from other module.
  // This mainly covers brand-new reports (default primaryModule='project' before the person has
  // touched the Data Source & Fields step) — once they start toggling modules there, that step seeds
  // and prunes selectedFields itself. It's a no-op while an existing report's fields are being loaded,
  // since that effect sets primaryModule and selectedFields together.
  useEffect(() => {
    if (catalog.fields.length > 0) {
      const hasCurrentFields = selectedFields.some((sf) => {
        const catF = catalog.fields.find((f) => f.key === sf.key)
        return catF && (catF.module === primaryModule || secondaryDatasets.includes(catF.module))
      })
      if (selectedFields.length === 0 || !hasCurrentFields) {
        const defaultFields = catalog.fields
          .filter((f) => f.module === primaryModule && f.defaultVisible)
          .map((f, index) => ({
            key: f.key,
            label: f.label,
            visible: true,
            format: f.dataType === 'currency' ? 'currency_inr' : null,
            width: null,
            order: index,
          }))
        if (defaultFields.length > 0) {
          setSelectedFields(defaultFields)
        }
      }
    }
  }, [primaryModule, catalog.fields])

  // Prefill active hub filters if passed — only for brand-new reports. An
  // existing report being edited keeps its own saved company/branch/filters
  // rather than picking up whatever the Reports Hub happens to be filtered
  // to right now.
  useEffect(() => {
    if (reportId) return
    if (initialPrefill?.filters) {
      const conds = []
      if (initialPrefill.filters.status && initialPrefill.filters.status.length > 0) {
        conds.push({
          field: 'project_status',
          operator: 'EQUALS',
          value: initialPrefill.filters.status[0],
          valueTo: null,
        })
      }
      if (conds.length > 0 && filterConditions.length === 0) {
        setFilterConditions(conds)
      }
    }
    if (initialPrefill?.companyId) setCompanyId(String(initialPrefill.companyId))
    if (initialPrefill?.branchId) setBranchId(String(initialPrefill.branchId))
    if (initialPrefill?.departmentId) setDepartmentId(String(initialPrefill.departmentId))
    if (initialPrefill?.projectId) setProjectId(String(initialPrefill.projectId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrefill, reportId])

  const buildConfigPayload = useCallback(() => {
    // Sanitize conditions: omit blank values unless operator is IS_BLANK or IS_NOT_BLANK
    const sanitizedConditions = filterConditions.filter((c) => {
      if (!c.field) return false
      if (c.operator === 'IS_BLANK' || c.operator === 'IS_NOT_BLANK') return true
      return c.value !== null && c.value !== undefined && String(c.value).trim() !== ''
    })

    return {
      primaryModule,
      secondaryDatasets,
      fields: selectedFields,
      filters: {
        logic: filterLogic,
        conditions: sanitizedConditions,
      },
      grouping,
      sorting,
      calculations,
      aggregations,
      visualization,
    }
  }, [
    primaryModule,
    secondaryDatasets,
    selectedFields,
    filterLogic,
    filterConditions,
    grouping,
    sorting,
    calculations,
    aggregations,
    visualization,
  ])

  // Trigger live preview
  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const config = buildConfigPayload()
      const data = await previewCustomReport(config, 50)
      setPreviewData(data)
    } catch (err) {
      setPreviewError(normalizeReportError(err, 'Failed to generate live preview.'))
    } finally {
      setPreviewLoading(false)
    }
  }, [buildConfigPayload])

  // Validation before advancing
  // Steps: 1 Setup · 2 Data Source & Fields · 3 Filters · 4 Grouping · 5 Calculations
  //        · 6 Aggregations · 7 Visualization · 8 Preview · 9 Save/Export
  const canProceed = () => {
    if (step === 1) {
      return reportName.trim().length > 0
    }
    if (step === 2) {
      return Boolean(primaryModule) && selectedFields.filter((f) => f.visible).length > 0
    }
    return true
  }

  const nextStep = () => {
    if (canProceed() && step < 9) {
      const next = step + 1
      setStep(next)
      if (next === 8) {
        fetchPreview()
      }
    }
  }

  const canGoToStep = useCallback(
    (targetStep) => {
      if (targetStep === 1) return true
      if (targetStep > 1 && !reportName.trim()) return false
      if (targetStep > 2 && (!primaryModule || !selectedFields.some((f) => f.visible))) return false
      return true
    },
    [reportName, primaryModule, selectedFields]
  )

  const goToStep = useCallback(
    (targetStep) => {
      if (targetStep === step) return
      if (!canGoToStep(targetStep)) return
      setStep(targetStep)
      if (targetStep === 8) {
        fetchPreview()
      }
    },
    [step, canGoToStep, fetchPreview]
  )

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const handleSave = async (saveAsNew = false) => {
    if (!reportName.trim()) {
      setSaveError('Report name is required.')
      setStep(1)
      return
    }

    setIsSaving(true)
    setSaveError(null)
    try {
      const payload = {
        reportName: saveAsNew ? `${reportName} (Copy)` : reportName,
        description,
        category,
        primaryModule,
        companyId: companyId ? Number(companyId) : null,
        branchId: branchId ? Number(branchId) : null,
        departmentId: departmentId ? Number(departmentId) : null,
        projectId: projectId ? Number(projectId) : null,
        visibility,
        config: buildConfigPayload(),
      }

      if (reportId && !saveAsNew) {
        await updateCustomReport(reportId, payload)
      } else {
        await createCustomReport(payload)
      }

      onSaved?.()
      onClose?.()
    } catch (err) {
      setSaveError(normalizeReportError(err, 'Failed to save custom report.'))
    } finally {
      setIsSaving(false)
    }
  }

  return {
    step,
    setStep,
    loadingCatalog,
    loadingReport,
    loadReportError,
    catalog,
    reportName,
    setReportName,
    description,
    setDescription,
    category,
    setCategory,
    companyId,
    setCompanyId,
    branchId,
    setBranchId,
    departmentId,
    setDepartmentId,
    projectId,
    setProjectId,
    visibility,
    setVisibility,
    primaryModule,
    setPrimaryModule,
    secondaryDatasets,
    setSecondaryDatasets,
    selectedFields,
    setSelectedFields,
    filterLogic,
    setFilterLogic,
    filterConditions,
    setFilterConditions,
    grouping,
    setGrouping,
    sorting,
    setSorting,
    calculations,
    setCalculations,
    aggregations,
    setAggregations,
    visualization,
    setVisualization,
    previewData,
    previewLoading,
    previewError,
    fetchPreview,
    canProceed,
    nextStep,
    prevStep,
    canGoToStep,
    goToStep,
    handleSave,
    isSaving,
    saveError,
    buildConfigPayload,
  }
}
